import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend, { type BackendError } from "@/utils/fetchToBackend";
import { authClient } from "@/lib/auth-client";
import {
  CulqiCheckoutCancelled,
  CulqiCheckoutError,
  openCulqiCheckout,
} from "@/lib/culqi";
import { betaKeys, billingKeys, viewQuotaKeys } from "@/lib/query-keys";
import { toastUtil } from "@/utils/toastUtil";
import { capture } from "@/lib/posthog";
import { planDisplayName } from "@/lib/format";
import type { BillingPlan } from "@/types/domain";

/**
 * Acciones de facturación. Las que cobran abren primero el checkout de Culqi para
 * tokenizar la tarjeta en el navegador y recién entonces llaman al backend.
 *
 * Todas invalidan también `betaKeys` y `viewQuotaKeys`: contratar o cambiar de plan
 * cambia la vigencia del acceso y el saldo de vistas del mes.
 */

// La tabla de precios no cambia por lo que haga el usuario, así que `plans` queda
// fuera; el resto sí depende de la suscripción.
function useInvalidateBilling() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: billingKeys.subscription() });
    qc.invalidateQueries({ queryKey: billingKeys.payments() });
    qc.invalidateQueries({ queryKey: betaKeys.info });
    qc.invalidateQueries({ queryKey: viewQuotaKeys.all });
  };
}

/** Mensaje a mostrar: Culqi redacta el suyo en español, tanto en el front como en el back. */
function billingErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof CulqiCheckoutError) return err.message;
  const backendError = err as BackendError;
  const hasOwnMessage =
    backendError?.errorCode === "PAYMENT_FAILED" ||
    backendError?.errorCode === "PAYMENTS_DISABLED";
  if (hasOwnMessage && backendError.message) return backendError.message;
  return backendError?.error ?? fallback;
}

/** Avisa del error, salvo si el usuario solo cerró el checkout de Culqi. */
function notifyBillingError(err: unknown, fallback: string) {
  if (err instanceof CulqiCheckoutCancelled) return;
  toastUtil(billingErrorMessage(err, fallback), "error");
}

/** Contrata un plan: token en el navegador, alta de la suscripción en el backend. */
export function useSubscribe() {
  const invalidate = useInvalidateBilling();
  const { data: session } = authClient.useSession();

  return useMutation({
    mutationFn: async (plan: BillingPlan) => {
      // Antes de abrir Culqi: es el único modo de ver quién llegó al checkout y
      // no pagó — la BD solo registra los cobros que sí ocurrieron.
      capture("checkout_opened", { plan: plan.plan_name });
      const tokenId = await openCulqiCheckout({
        // El primer cobro es el de lanzamiento si el plan lo tiene; el salto al
        // precio regular lo aplica Culqi con los `initial_cycles`.
        amount: plan.plan_promoamount ?? plan.plan_priceamount!,
        currency: plan.plan_currency,
        title: `Licitigo ${planDisplayName(plan)}`,
        email: session?.user.email ?? "",
      });
      // El usuario ya entregó su tarjeta: es la última señal de intención antes
      // de que el cobro dependa de Culqi y no de él.
      capture("checkout_submitted", { plan: plan.plan_name });
      return fetchToBackend("/subscriptions/checkout", {
        method: "POST",
        body: JSON.stringify({ planId: plan.plan_id, tokenId }),
      });
    },
    onSuccess: (_data, plan) => {
      // La compra hacia Meta NO sale de acá: la manda el servidor desde el webhook
      // de Culqi, que es el único que sabe que el cobro de verdad ocurrió.
      capture("subscription_created", { plan: plan.plan_name });
      invalidate();
      toastUtil("¡Listo! Tu plan ya está activo.", "success");
    },
    onError: (err) => notifyBillingError(err, "No se pudo activar tu plan."),
  });
}

/**
 * Cambia de plan. Culqi no permite mover el plan de una suscripción, así que el
 * backend cancela y crea otra: el cobro es inmediato y el ciclo se reinicia hoy.
 */
export function useChangePlan() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (planId: number) =>
      fetchToBackend("/subscriptions/change-plan", {
        method: "POST",
        body: JSON.stringify({ planId }),
      }),
    onSuccess: (_data, planId) => {
      capture("plan_changed", { plan_id: planId });
      invalidate();
      toastUtil("Plan actualizado.", "success");
    },
    onError: (err) => notifyBillingError(err, "No se pudo cambiar de plan."),
  });
}

/** Cancela la renovación. El acceso sigue vigente hasta el fin del periodo pagado. */
export function useCancelSubscription() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () =>
      fetchToBackend("/subscriptions/cancel", { method: "POST" }),
    onSuccess: () => {
      capture("subscription_cancelled");
      invalidate();
      toastUtil(
        "Cancelamos la renovación. Mantienes el acceso hasta el fin del periodo.",
        "success",
      );
    },
    onError: (err) =>
      notifyBillingError(err, "No se pudo cancelar la suscripción."),
  });
}

/**
 * Pasa la cuenta al plan Free de inmediato. Solo aplica tras un cobro rechazado: es
 * la alternativa a arreglar la tarjeta para quien no quiere seguir pagando.
 */
export function useDowngradeToFree() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () =>
      fetchToBackend("/subscriptions/downgrade", { method: "POST" }),
    onSuccess: () => {
      capture("downgraded_to_free");
      invalidate();
      toastUtil("Tu cuenta pasó al plan Free.", "success");
    },
    onError: (err) => notifyBillingError(err, "No se pudo cambiar al plan Free."),
  });
}

/** Reemplaza la tarjeta con la que se cobra la renovación. */
export function useUpdateCard() {
  const invalidate = useInvalidateBilling();
  const { data: session } = authClient.useSession();

  return useMutation({
    mutationFn: async () => {
      // Culqi exige monto y moneda para abrir el checkout aunque aquí solo
      // tokenicemos: el cobro real lo hace la suscripción, no este token.
      const tokenId = await openCulqiCheckout({
        amount: 300,
        currency: "PEN",
        title: "Actualizar tarjeta",
        email: session?.user.email ?? "",
      });
      return fetchToBackend("/subscriptions/card", {
        method: "POST",
        body: JSON.stringify({ tokenId }),
      });
    },
    onSuccess: () => {
      capture("card_updated");
      invalidate();
      toastUtil("Tarjeta actualizada.", "success");
    },
    onError: (err) =>
      notifyBillingError(err, "No se pudo actualizar la tarjeta."),
  });
}
